import { EnvironmentAPI } from "@/api/EnvironmentAPI";
import { Brand } from "./Brand";
import { Store } from "./Store";
import * as Yup from "yup";

const { BaseStorage } = require("@/storage/BaseStorage");

const storageKey = "of-environment";

const eventKey = `${storageKey}:changed`;

export class EnvironmentConfiguration {
  /**
   * @param {Object} options - Opções de configuração
   * @param {string} options.unitCode - Código da unidade
   * @param {string} options.tableService - Serviço de mesas
   * @param {boolean} options.allowChallenge - Permite exibir desafio de proteção infantil
   * @param {BaseStorage} options.storage - Instancia do serviço de armazenamento
   * 
   */
  constructor({ unitCode, tableService, allowChallenge, storage }) {
    this.storage = storage;

    /**
     * Código da unidade.
     * @type {string}
     */
    this.unitCode = unitCode;

    this.tableService = tableService;

    /**
     * Permite exibir desafio de proteção infantil
     * @type {boolean}
     */
    this.allowChallenge = allowChallenge;
  }

  static fromStorage() {
    const storage = new BaseStorage({ key: storageKey });

    const configs = storage.getData();

    const tableConfiguration = new EnvironmentConfiguration({
      ...configs,
      storage,
    });

    return tableConfiguration;
  }

  static async loadEnvironment({ unitCode }) {
    const environmentResponse = await EnvironmentAPI.getEnvironmentByCode(unitCode);

    const data = environmentResponse.getData({});
    const brandAlias = data.brandAlias;
    const storeAlias = data.unitAlias;

    if (environmentResponse.getErrors([]).length > 0) {
      throw new Error(`${environmentResponse.getErrors([])?.[0]}`);
    }

    await Brand.install(brandAlias);
    await Store.install(storeAlias);

    Brand.alias = brandAlias;
    Store.alias = storeAlias;
  }

  update(configs) {
    const currentConfigs = this.storage.getData() || {};

    this.storage.setData({
      ...currentConfigs,
      ...configs,
    });

    if (window) {
      window.dispatchEvent(
        new CustomEvent(eventKey, {
          detail: { storage: this.storage.id, key: this.storage.getKey() },
        })
      );
    }
  }

  async import (rawConfig) {
    try {
      const configObject = JSON.parse(rawConfig);

      const schema = Yup.object({
        unitCode: Yup.string()
          .required("Campo obrigatório"),
        allowChallenge: Yup.boolean()
          .required("Campo obrigatório")
      });

      const configs = schema.validateSync(configObject, { strict: true });

      await EnvironmentConfiguration.loadEnvironment({
        unitCode: configs.unitCode
      });

      this.update(configs);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const field = err.path;
        const error = err.errors[0];
        const errorMessage = [field, error].filter(Boolean).join(" - ");
        throw new Error(errorMessage);
      }

      throw new Error(err.message);
    }
  }

  export () {
    const { storage, listener, ...configs } = this;
    return { ...configs };
  }

  addListener(handler) {
    if (window && !this.listener) {
      const listener = window.addEventListener(eventKey, (event) => {
        if (event.detail.key === this.storage.getKey()) {
          handler();
        }
      });

      this.listener = listener;
    }
  }

  removeListener() {
    if (window && this.listener) {
      window.removeEventListener(eventKey, this.listener);
      this.listener = null;
    }
  }
}
